import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Plus, Trash2, Save, Send, AlertTriangle } from 'lucide-react';
import client from '../../api/client';
import SearchableSelect from '../../shared/components/SearchableSelect';
import { NAVY, AMBER, BORDER_COLOR, bubbleStyle, navyBtn, fieldStyle } from '../../styles/tokens';
import { etiquetaProyecto } from '../../utils/formatProject';

// VNA-129 — "Mi nómina": el personal de Oficina carga SU semana en una grilla (filas =
// proyecto + tarea, columnas = días). Guardar la convierte en tarjetas diarias normales
// (backend: miSemanaController); Someter semana manda cada día a Revisión Final con el
// endpoint de siempre, así que valen las mismas reglas (8 h/día, 40 h/semana, revisores).

const DIAS = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
const EDITABLES = ['draft', 'returned'];
const ESTADO = {
  draft:           { label: 'Borrador', color: '#7c3aed' },
  returned:        { label: 'Devuelto', color: '#ea580c' },
  submitted:       { label: 'Enviado',  color: '#ca8a04' },
  pending_manager: { label: 'Enviado',  color: '#ca8a04' },
  pending_clerk:   { label: 'Enviado',  color: '#ca8a04' },
  approved:        { label: 'Aprobado', color: '#16a34a' },
};
const hoyISO = () => new Date().toISOString().slice(0, 10);
const sumarDias = (iso, n) => { const d = new Date(`${iso}T12:00:00Z`); d.setUTCDate(d.getUTCDate() + n); return d.toISOString().slice(0, 10); };
const num = v => Math.round((parseFloat(v) || 0) * 100) / 100;
let siguienteId = 1;

export default function MiSemanaPage() {
  const [semanaRef, setSemanaRef] = useState(hoyISO());
  const [data, setData] = useState(null);        // respuesta del servidor
  const [filas, setFilas] = useState([]);        // copia editable: [{key, project_id, cost_code_id, gl_code_id, hours}]
  const [sucio, setSucio] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [trabajando, setTrabajando] = useState(false);
  const [proyectos, setProyectos] = useState([]);
  const [codigos, setCodigos] = useState({});    // project_id → [{id,label}]
  const [glCuentas, setGlCuentas] = useState([]);
  const [checks, setChecks] = useState({});      // fecha → hours-check del día

  const cargar = useCallback(async (ref) => {
    setError(''); setAviso('');
    try {
      const r = await client.get('/api/nomina/mi-semana', { params: { week: ref } });
      setData(r.data);
      setFilas(r.data.rows.map(f => ({ ...f, key: siguienteId++ })));
      setSucio(false);
    } catch (err) {
      setData(null);
      setError(err.response?.data?.error || 'No se pudo cargar tu semana.');
    }
  }, []);

  useEffect(() => { cargar(semanaRef); }, [semanaRef, cargar]);
  useEffect(() => {
    // /api/projects trae nomina_coding_mode (tarea vs cuenta GL); my-projects no.
    client.get('/api/projects').then(r => setProyectos((r.data || []).filter(p => p.active !== false))).catch(() => setProyectos([]));
    client.get('/api/nomina/gl-accounts').then(r => setGlCuentas(r.data || [])).catch(() => setGlCuentas([]));
  }, []);

  // Avisos por día: otras tarjetas donde aparece, ausencias aprobadas (mismo endpoint que la tarjeta diaria).
  useEffect(() => {
    if (!data?.persona) return;
    Promise.all(data.days.map(d =>
      client.post('/api/nomina/hours-check/batch', { employeeIds: [data.persona.id], date: d.date })
        .then(r => [d.date, r.data?.[data.persona.id] || null]).catch(() => [d.date, null])))
      .then(pares => setChecks(Object.fromEntries(pares)));
  }, [data]);

  // Códigos por proyecto (tareas vinculadas); las cuentas GL son una sola lista.
  useEffect(() => {
    const faltan = [...new Set(filas.map(f => f.project_id).filter(Boolean))].filter(pid => !codigos[pid]);
    faltan.forEach(pid => client.get('/api/nomina/cost-codes', { params: { project_id: pid } })
      .then(r => setCodigos(prev => ({ ...prev, [pid]: (r.data || []).map(c => ({ id: c.id, label: `${c.code} ${c.description || ''}` })) })))
      .catch(() => setCodigos(prev => ({ ...prev, [pid]: [] }))));
  }, [filas, codigos]);

  useEffect(() => {
    const salir = e => { if (sucio) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', salir);
    return () => window.removeEventListener('beforeunload', salir);
  }, [sucio]);

  const hoja = useCallback((pid, fecha) => data?.sheets.find(s => s.project_id === pid && s.sheet_date === fecha), [data]);
  const bloqueada = useCallback((pid, fecha) => {
    if (fecha > hoyISO()) return true;
    const s = hoja(pid, fecha);
    return !!s && !EDITABLES.includes(s.status);
  }, [hoja]);

  const proyecto = pid => proyectos.find(p => p.id === pid);
  const esGl = pid => proyecto(pid)?.nomina_coding_mode === 'gl_accounts';

  function cambiar(key, cambios) {
    setFilas(prev => prev.map(f => f.key === key ? { ...f, ...cambios } : f));
    setSucio(true);
  }
  function cambiarHora(key, fecha, campo, valor) {
    setFilas(prev => prev.map(f => f.key !== key ? f : {
      ...f, hours: { ...f.hours, [fecha]: { regular: 0, overtime: 0, ...f.hours[fecha], [campo]: valor } },
    }));
    setSucio(true);
  }

  const totalesDia = useMemo(() => Object.fromEntries((data?.days || []).map(d => [d.date, filas.reduce((acc, f) => ({
    r: acc.r + num(f.hours[d.date]?.regular), e: acc.e + num(f.hours[d.date]?.overtime),
  }), { r: 0, e: 0 })])), [filas, data]);
  const totalSemana = Object.values(totalesDia).reduce((a, t) => ({ r: a.r + t.r, e: a.e + t.e }), { r: 0, e: 0 });

  // Lo que el servidor ya tiene guardado ese día (para separar "otras tarjetas" de las mías).
  const guardadoDia = fecha => (data?.rows || []).reduce((s, f) => s + num(f.hours[fecha]?.regular), 0);

  function cambiarSemana(delta) {
    if (sucio && !window.confirm('Tienes cambios sin guardar en esta semana. ¿Salir sin guardar?')) return;
    setSemanaRef(sumarDias(data?.week_start || semanaRef, delta * 7));
  }

  async function guardar() {
    setTrabajando(true); setError(''); setAviso('');
    try {
      const r = await client.put('/api/nomina/mi-semana', {
        week_start: data.week_start,
        rows: filas.map(f => ({ project_id: f.project_id, cost_code_id: f.cost_code_id ?? null, gl_code_id: f.gl_code_id ?? null, hours: f.hours })),
      });
      setData(r.data);
      setFilas(r.data.rows.map(f => ({ ...f, key: siguienteId++ })));
      setSucio(false);
      setAviso('✓ Guardado');
      return r.data;
    } catch (err) {
      setError(err.response?.data?.error || 'No se pudo guardar.');
      return null;
    } finally {
      setTrabajando(false);
    }
  }

  async function someterSemana() {
    const guardada = await guardar();
    if (!guardada) return;
    const pendientes = guardada.sheets.filter(s => EDITABLES.includes(s.status));
    if (pendientes.length === 0) { setAviso('No hay días por someter en esta semana.'); return; }
    setTrabajando(true);
    let enviados = 0;
    let fallo = '';
    for (const s of pendientes) {
      try {
        await client.post(`/api/nomina/sheets/${s.id}/submit`);
        enviados++;
      } catch (err) {
        const d = err.response?.data;
        const msg = d?.error === 'REGULAR_CAP_EXCEEDED'
          ? 'Pasas el tope de horas regulares (8 por día o 40 por semana). Mueve el exceso a extra.'
          : (d?.error || 'No se pudo someter.');
        fallo = `${s.sheet_date}: ${msg}${enviados ? ` (${enviados} día(s) ya se enviaron)` : ''}`;
        break;
      }
    }
    setTrabajando(false);
    // ⚠ Revisión final: `cargar` limpia el error al empezar; el motivo se muestra DESPUÉS, o
    // el usuario ve días a medio enviar sin saber por qué.
    await cargar(guardada.week_start);
    if (fallo) setError(fallo);
    else setAviso(`✓ Semana enviada a Revisión Final (${enviados} tarjeta(s)).`);
  }

  if (!data) {
    return (
      <div style={{ padding: 24 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: NAVY }}>Mi nómina</h1>
        {error ? (
          <div style={{ ...bubbleStyle, color: '#991b1b', background: '#fef2f2' }}>{error}</div>
        ) : <div style={{ color: '#94a3b8' }}>Cargando…</div>}
      </div>
    );
  }

  const fueraDeSemana = data.week_start > hoyISO();
  const devueltos = data.sheets.filter(s => s.status === 'returned');
  const opcionesProyecto = proyectos.map(p => ({ id: p.id, label: etiquetaProyecto(p.code, p.name) }));
  const opcionesGl = glCuentas.map(g => ({ id: g.id, label: `${g.code} ${g.name || ''}` }));

  return (
    <div style={{ padding: '20px 24px', maxWidth: 1180 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: NAVY, margin: 0 }}>Mi nómina</h1>
        <button onClick={() => cambiarSemana(-1)} style={btnIcono} title="Semana anterior"><ChevronLeft size={16} /></button>
        <span style={{ fontWeight: 600, color: NAVY }}>{data.week_start} → {data.week_end}</span>
        <button onClick={() => cambiarSemana(1)} disabled={sumarDias(data.week_start, 7) > hoyISO()} style={btnIcono} title="Semana siguiente"><ChevronRight size={16} /></button>
        <span style={{ fontSize: 12, color: '#64748b' }}>{data.persona.name}</span>
      </div>

      {/* UAT 2026-10-29 (Marcel): la lógica de reenvío tiene que estar a la vista. */}
      <div style={{ fontSize: 12, color: '#475569', background: '#f8fafc', border: `1px solid ${BORDER_COLOR}`, borderRadius: 8, padding: '8px 12px', marginBottom: 10, lineHeight: 1.45 }}>
        <strong>Cómo se envía:</strong> cada proyecto de cada día es una tarjeta aparte. Al someterla, ese proyecto de ese
        día queda bloqueado hasta que Revisión Final lo apruebe o te lo devuelva; si te lo devuelve, lo corriges y vuelves a
        someter. Puedes agregar otro proyecto a un día ya enviado: va como una tarjeta nueva. «Someter semana» envía solo lo
        que está en Borrador o Devuelto.
      </div>

      {devueltos.map(s => (
        <div key={s.id} style={{ ...bubbleStyle, padding: '8px 12px', marginBottom: 8, borderLeft: '3px solid #ea580c', fontSize: 13 }}>
          <AlertTriangle size={13} style={{ verticalAlign: -2, color: '#ea580c' }} /> Devuelto el {s.sheet_date}
          {proyecto(s.project_id) ? ` (${proyecto(s.project_id).name})` : ''}: <strong>{s.return_reason || 'sin motivo'}</strong>. Corrígelo y vuelve a someter.
          {/* UAT 2026-10-29: la nota de Revisión Final dice QUÉ corregir; sin ella solo había el motivo. */}
          {s.reviewer_notes && (
            <div style={{ marginTop: 4, color: '#475569' }}>Nota de Revisión Final: “{s.reviewer_notes}”</div>
          )}
        </div>
      ))}

      <div style={{ ...bubbleStyle, padding: 0, overflowX: 'auto' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
          <thead>
            <tr style={{ background: '#f8fafc' }}>
              <th style={th}>Proyecto</th>
              <th style={th}>Tarea / Cuenta GL</th>
              {data.days.map((d, i) => {
                const delDia = data.sheets.filter(s => s.sheet_date === d.date);
                const est = delDia.find(s => s.status === 'returned') || delDia.find(s => s.status === 'draft') || delDia[0];
                const chk = checks[d.date];
                const otras = chk ? Math.max(0, num(chk.total_regular) - guardadoDia(d.date)) : 0;
                return (
                  <th key={d.date} style={{ ...th, textAlign: 'center', minWidth: 92, background: d.is_holiday ? '#f5f3ff' : d.is_weekend ? '#f1f5f9' : undefined }}>
                    <div>{DIAS[i]} {d.date.slice(8)}</div>
                    {d.is_holiday && <div style={{ fontSize: 10, color: '#6d28d9' }}>{d.holiday_name}</div>}
                    {est && <div style={{ fontSize: 10, fontWeight: 700, color: ESTADO[est.status]?.color }}>{ESTADO[est.status]?.label || est.status}</div>}
                    {otras > 0 && <div title="Horas tuyas en tarjetas de otras personas ese día" style={{ fontSize: 10, color: '#92400e' }}>⚠ Otras: {otras}h</div>}
                    {chk?.pto_absences?.length > 0 && <div style={{ fontSize: 10, color: '#0f766e' }}>Ausencia aprobada</div>}
                  </th>
                );
              })}
              <th style={th} />
            </tr>
          </thead>
          <tbody>
            {filas.map(f => {
              const conBloqueo = data.days.some(d => bloqueada(f.project_id, d.date) && d.date <= hoyISO() && hoja(f.project_id, d.date));
              return (
                <tr key={f.key} style={{ borderTop: `1px solid ${BORDER_COLOR}` }}>
                  <td style={{ ...td, minWidth: 180 }}>
                    {f.project_name && !f.nuevo ? f.project_name : (
                      <SearchableSelect items={opcionesProyecto} value={f.project_id || null}
                        onChange={pid => cambiar(f.key, { project_id: pid, project_name: proyecto(pid)?.name, cost_code_id: null, gl_code_id: null })}
                        placeholder="Elige el proyecto" />
                    )}
                  </td>
                  <td style={{ ...td, minWidth: 200 }}>
                    {f.code_label && !f.nuevo ? f.code_label : (
                      <SearchableSelect
                        items={esGl(f.project_id) ? opcionesGl : (codigos[f.project_id] || [])}
                        value={(esGl(f.project_id) ? f.gl_code_id : f.cost_code_id) || null}
                        onChange={id => cambiar(f.key, esGl(f.project_id) ? { gl_code_id: id, cost_code_id: null } : { cost_code_id: id, gl_code_id: null })}
                        disabled={!f.project_id}
                        placeholder={esGl(f.project_id) ? 'Elige la cuenta GL' : 'Elige la tarea'} />
                    )}
                  </td>
                  {data.days.map(d => {
                    const ro = !f.project_id || bloqueada(f.project_id, d.date);
                    const h = f.hours[d.date] || {};
                    const enviada = f.project_id && hoja(f.project_id, d.date) && !EDITABLES.includes(hoja(f.project_id, d.date).status);
                    const motivoBloqueo = d.date > hoyISO() ? 'Día futuro: todavía no se puede cargar.'
                      : enviada ? 'Ya enviado: solo se puede cambiar si Revisión Final lo devuelve.' : undefined;
                    return (
                      <td key={d.date} title={motivoBloqueo} style={{ ...td, textAlign: 'center' }}>
                        <input type="number" min="0" step="0.5" value={h.regular ?? ''} disabled={ro} title="Regulares"
                          onChange={e => cambiarHora(f.key, d.date, 'regular', e.target.value)} style={celda} />
                        <input type="number" min="0" step="0.5" value={h.overtime ?? ''} disabled={ro} title="Extra"
                          onChange={e => cambiarHora(f.key, d.date, 'overtime', e.target.value)} style={{ ...celda, marginTop: 2, background: '#fffbeb' }} />
                      </td>
                    );
                  })}
                  <td style={td}>
                    {!conBloqueo && (
                      <button onClick={() => { setFilas(prev => prev.filter(x => x.key !== f.key)); setSucio(true); }} style={btnIcono} title="Quitar fila">
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr style={{ borderTop: `2px solid ${BORDER_COLOR}`, background: '#f8fafc', fontWeight: 700 }}>
              <td style={td} colSpan={2}>Total (R / E)</td>
              {data.days.map(d => (
                <td key={d.date} style={{ ...td, textAlign: 'center', color: totalesDia[d.date].r > 8 ? '#dc2626' : NAVY }}>
                  {totalesDia[d.date].r} / {totalesDia[d.date].e}
                </td>
              ))}
              <td style={td} />
            </tr>
          </tbody>
        </table>
        <div style={{ padding: '10px 12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
          <button onClick={() => { setFilas(prev => [...prev, { key: siguienteId++, nuevo: true, project_id: null, cost_code_id: null, gl_code_id: null, hours: {} }]); setSucio(true); }}
            disabled={fueraDeSemana} style={{ ...btnSecundario }}>
            <Plus size={14} /> Agregar fila
          </button>
          <div style={{ fontSize: 13, fontWeight: 700, color: totalSemana.r > 40 ? AMBER : NAVY }}>
            Semana: {totalSemana.r} h regulares{totalSemana.r > 40 ? ' (más de 40)' : ''} · {totalSemana.e} h extra
          </div>
        </div>
      </div>

      {error && <div style={{ marginTop: 12, padding: '8px 12px', borderRadius: 6, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', fontSize: 13 }}>{error}</div>}
      {aviso && !error && <div style={{ marginTop: 12, fontSize: 13, color: '#16a34a' }}>{aviso}</div>}

      <div style={{ display: 'flex', gap: 10, marginTop: 14, justifyContent: 'flex-end' }}>
        {sucio && <span style={{ alignSelf: 'center', fontSize: 12, color: '#b45309' }}>Cambios sin guardar</span>}
        <button onClick={guardar} disabled={trabajando} style={{ ...btnSecundario }}><Save size={14} /> Guardar</button>
        <button onClick={someterSemana} disabled={trabajando} style={{ ...navyBtn, display: 'flex', alignItems: 'center', gap: 6, opacity: trabajando ? 0.6 : 1 }}>
          <Send size={14} /> Someter semana
        </button>
      </div>
    </div>
  );
}

const th = { padding: '8px 6px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#475569', verticalAlign: 'top' };
const td = { padding: '6px', verticalAlign: 'top' };
const celda = { ...fieldStyle, width: 56, padding: '3px 4px', fontSize: 12, textAlign: 'center' };
const btnIcono = { background: 'none', border: `1px solid ${BORDER_COLOR}`, borderRadius: 6, padding: '3px 6px', cursor: 'pointer', color: NAVY, display: 'inline-flex', alignItems: 'center' };
const btnSecundario = { ...btnIcono, padding: '6px 12px', gap: 6, fontSize: 13, fontWeight: 600 };
